import { useEffect, useMemo, useRef, useState } from "react";
import { foyerApi } from "../../http/foyer";
import type { Asset } from "@shared/types/content-api";

/**
 * Resolve a list of catalogue ids to their rows, for the surfaces that hold
 * `assetIds` and need to draw something a person recognises.
 *
 * ## Why this is a hook and not two copies
 *
 * The carousel editor now has TWO surfaces over the same picks — the ordered
 * list an operator drags, and the preview beneath it — and before this hook the
 * preview fetched its own. Two components reading the same ids independently is
 * how a rail ends up showing a title in one place and not the other, and it
 * doubles the reads against a catalogue we do not own. The dialog resolves
 * once, at the top, and hands the rows down.
 *
 * ## 🚨 A channel needs a SECOND read, because the VOD one refuses it
 *
 * `readTitle` on the server ends with `if (!isVodAssetType(asset.type)) return
 * null`, deliberately — without it a channel would be addressable, and
 * PATCHable, through routes whose defaults are wrong for it. So a curated
 * carousel holding a channel id (which is legitimate: `AssetType` includes
 * `channel`, and the read path filters purely by `assetId`) gets a 404 from
 * `getVodTitle` and would draw as "no longer in the catalogue".
 *
 * The fallback is the channel route. It costs a second read for channel ids
 * only, and it is the honest shape rather than the cheap one: there is no CMS
 * route that reads any asset by id regardless of type. The same list filter
 * that collapses this whole hook to one call would remove the fallback too.
 *
 * ## 🚨 One read per id, and why
 *
 * `GET /distribution/vod-titles` has **no `assetId` filter**. The Content API
 * itself does — `carouselResolver` filters a curated rail with exactly that
 * parameter — but Foyer's own list route does not expose it, so the only way to
 * turn N ids into N rows from the CMS is N point reads.
 *
 * That is a route change with real conflict rules to settle (`readListQuery`
 * refuses `externalId` beside every other filter, and `assetId` would need the
 * same treatment), so it is deliberately NOT smuggled in here. When it lands,
 * this hook becomes one list call and {@link TITLE_LOOKUP_CAP} goes away —
 * every caller keeps its current shape.
 *
 * ## The cap does not limit what you can DO, only what you can SEE
 *
 * Past the cap an id is returned in `unresolved` rather than dropped. The list
 * still renders it, still drags it and still removes it — a 61st pick is fully
 * editable, it just shows its id instead of its poster. Capping the *editing*
 * would be the silent kind of limit; capping the *enrichment* is visible in the
 * row itself.
 */

/**
 * How many ids are worth resolving one-at-a-time.
 *
 * 60 is a judgement, not a measurement: it covers every rail anybody has
 * authored by hand while keeping a dialog open from costing three figures of
 * reads against a shared production catalogue.
 */
export const TITLE_LOOKUP_CAP = 60;

export interface VodTitleLookup {
  /** Only ids that resolved. A missing id is a real answer, not an error. */
  byId: Map<string, Asset>;
  loading: boolean;
  /** Ids past {@link TITLE_LOOKUP_CAP}, never looked up. */
  unresolved: string[];
}

const EMPTY: Asset[] = [];

/**
 * One id, whichever kind of asset it turns out to be.
 *
 * VOD first because the overwhelming majority of picks are titles, so the
 * second call is paid only by channels. A failure on BOTH is `null` — a real
 * answer meaning "nothing in the catalogue answers to this id", which the row
 * renders as such rather than as an error.
 */
export async function readAnyAsset(id: string): Promise<Asset | null> {
  const title = await foyerApi.getVodTitle(id).catch(() => null);
  if (title) return title;
  return foyerApi.getDistributionChannel(id).catch(() => null);
}

export function useVodTitles(ids: string[]): VodTitleLookup {
  const [rows, setRows] = useState<Asset[]>(EMPTY);
  const [loading, setLoading] = useState(false);

  // Joined, because these components re-render on every keystroke and the array
  // identity changes every time while the PICKS almost never do.
  const key = ids.join(",");
  const wanted = useMemo(
    () => (key ? key.split(",").slice(0, TITLE_LOOKUP_CAP) : []),
    [key]
  );

  // A run counter rather than a boolean: two overlapping fetches must not let
  // the slower one win, and `cancelled` alone cannot express "a newer one
  // started" once the effect has already been torn down and re-run.
  const run = useRef(0);

  useEffect(() => {
    const mine = ++run.current;
    if (wanted.length === 0) {
      setRows(EMPTY);
      setLoading(false);
      return;
    }
    setLoading(true);
    void (async () => {
      const results = await Promise.all(
        // Per id, never as a whole: one withdrawn title must not blank the
        // list. An id that no longer resolves is exactly the case an operator
        // opened this dialog to find.
        wanted.map((id) => readAnyAsset(id))
      );
      if (run.current !== mine) return;
      setRows(results.filter((r): r is Asset => Boolean(r)));
      setLoading(false);
    })();
  }, [wanted]);

  return useMemo(
    () => ({
      byId: new Map(rows.map((r) => [r.assetId, r])),
      loading,
      unresolved: ids.slice(TITLE_LOOKUP_CAP),
    }),
    // `key` stands in for `ids` — same reason as above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, loading, key]
  );
}
